import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../../layouts/AppLayout';
import { useParams } from 'react-router-dom';
import InvalidUrlComponent from '../../components/InvalidUrl/InvalidUrlComponent';
import IssueDetailsCard from '../../components/IssueDetails/IssueDetailsCard';
import { Box, Typography } from '@mui/material';
import { fetchData } from '../../utils/apiClient';
import { IssueQuestionDetails, ProductDetails, QuestionProductDetails, TimeTracking } from '../../types/question';
import NoSearchResultIcon from '../../assets/NoSearchResultIcon';
import { usePermissions } from '../../provider/PermissionsProvider';

interface IssueResponse {
  issue: {
    jiraId: string;
    crmCaseId: string;
    dataMasterQuestionId: string;
    status: string;
    issueId: string;
    createdAt: string;
    updatedAt: string;
    svnVersion?: number;
    wasVersion?: number;
    data?: {
      qcardTitle: string;
      qcardId: string;
    };
    crmSubject: string;
  };
  questionDetails: object;
  productDetails: ProductDetails;
  questionProductDetails: QuestionProductDetails;
  timeTracking: TimeTracking;
  authoredByVendor?: string;
}

const IssueDetailsContainer: React.FC = () => {
  const { jiraId, caseId, questionId, wasIssueId } = useParams<{
    jiraId: string;
    caseId: string;
    questionId: string;
    wasIssueId: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [issueData, setIssueData] = useState<IssueQuestionDetails | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const { permissions } = usePermissions();

  const fetchIssueData = useCallback(async () => {
    if (!jiraId || !questionId) return;

    setLoading(true);
    setError(null);
    try {
      let data: IssueResponse;

      if (wasIssueId) {
        data = await fetchData(`/issues?jiraId=${jiraId}&masterQuestionId=${questionId}&wasIssueId=${wasIssueId}&issueId=${wasIssueId}`, 'GET_ISSUE') as IssueResponse;
      }else{
        if (!caseId) {
          data = await fetchData(`/issues?jiraId=${jiraId}&masterQuestionId=${questionId}`, 'GET_ISSUE') as IssueResponse;
        } else {
          data = await fetchData(`/issues?caseId=${caseId}&masterQuestionId=${questionId}&jiraId=${jiraId}`, 'GET_ISSUE') as IssueResponse;
        }
      }
      if (!data.issue) {
        setError('No question found for the provided parameters.');
        setLoading(false);
        return;
      }

      const issue = data.issue;
      // Map API response to IssueQuestionDetails fields
      setIssueData({
        jiraId: issue.jiraId || '',
        caseId: issue.crmCaseId || '',
        wasQuestionId: issue.dataMasterQuestionId || '',
        status: issue.status === 'OPEN' ? 'Open' : 'Close',
        issueId: issue.issueId || '',
        createdAt: issue.createdAt || '',
        updatedAt: issue.updatedAt || '',
        title: issue.crmSubject || '',
        productDetails: data.productDetails || {},
        questionDetails: data.questionDetails || {},
        questionProductDetails: data.questionProductDetails || {},
        svnVersion: issue.svnVersion,
        wasVersion: issue.wasVersion,
        timeTracking: data.timeTracking || null,
        authoredByVendor: data.authoredByVendor || '',
      });

    } catch (e: any) {
      setError(e.message || 'Error fetching issue details');
      setLoading(false);
    }finally {
      setLoading(false);
    }
  }, [jiraId, caseId, questionId, wasIssueId]);

  useEffect(() => {
    fetchIssueData();
  }, [fetchIssueData]);

  if (!jiraId || !questionId) {
    return (
      <InvalidUrlComponent message="The issue doesn't exists" href="/" />
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ p: { xs: 1.5, sm: 3 }, textAlign: 'center' }}>
          <IssueDetailsCard 
            issueData={issueData} 
            permissions={permissions}
            loading={loading} 
            onDataRefresh={fetchIssueData} 
          />
        </Box>
      </AppLayout>
    );
  }

  if (error || !issueData) {
    return (
      <AppLayout>
        <Box sx={{ p: { xs: 1.5, sm: 3 }, textAlign: 'center' }}>
          {
            !issueData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <NoSearchResultIcon sx={{ width: { xs: 260, sm: 400 }, height: { xs: 140, sm: 200 } }} />
              </Box>
            )
          }
          <Typography variant="h6" color="error">
            {error || 'Question not found for the provided parameters.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            JIRA ID: {jiraId}, Case ID: {caseId}, Question ID: {questionId}
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <IssueDetailsCard 
        issueData={issueData} 
        loading={loading} 
        permissions={permissions} 
        onDataRefresh={fetchIssueData} 
      />
    </AppLayout>
  );
};

export default IssueDetailsContainer;
